import type { CreateTeamPayload } from "@/hooks/useTeams"

/**
 * How a team created from the admin panel gets access. Mirrors the backend's
 * `createTeamSchema`: a comp OR a trial OR nothing — never a bare plan, because
 * that was a database-only grant a Stripe webhook could silently revoke.
 */
export type TeamAccessMode = "comp" | "trial" | "none"

export type TeamAccessChoice = {
  mode: TeamAccessMode
  /** Comp only. */
  planId: string
  /** Comp only. `yyyy-mm-dd` from a date input; empty means no end date. */
  expiresAt: string
  /** Comp only. Required — it is what marks the subscription as a comp. */
  reason: string
}

/**
 * A `<input type="date">` value as an instant: midnight at the START of that
 * calendar day in the admin's local time zone. `new Date("2027-09-16")` would
 * parse as midnight UTC, which for anyone west of UTC is the evening of the
 * 15th — the comp would end a day early and display as the previous date.
 * Returns null for an empty or unparseable value.
 */
export function dateInputToLocalStart(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!match) return null
  const [, y, m, d] = match
  const date = new Date(Number(y), Number(m) - 1, Number(d))
  // The Date constructor rolls invalid components over (month 13 → January), so
  // check that what came back is the day that was asked for.
  const roundTrips =
    date.getFullYear() === Number(y) &&
    date.getMonth() === Number(m) - 1 &&
    date.getDate() === Number(d)
  return roundTrips ? date : null
}

/** ISO instant for a date input, or null when empty. Throws on an unparseable value. */
export function dateInputToIso(value: string): string | null {
  if (!value.trim()) return null
  const date = dateInputToLocalStart(value)
  if (!date) throw new Error(`Not a yyyy-mm-dd date: ${value}`)
  return date.toISOString()
}

/** `yyyy-mm-dd` for the same calendar day one year from `now`, in local time. */
export function defaultCompEndDate(now: Date = new Date()): string {
  const date = new Date(now)
  date.setFullYear(date.getFullYear() + 1)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

/**
 * The starting state of the chooser. Comp is the default because it is what an
 * admin creating a team almost always means by "give them a plan"; the trial
 * and free-tier options are one click away and the submit button says which
 * one will happen.
 */
export function defaultTeamAccessChoice(now: Date = new Date()): TeamAccessChoice {
  return { mode: "comp", planId: "team", expiresAt: defaultCompEndDate(now), reason: "" }
}

/**
 * Why the current choice cannot be submitted, or null when it can. Checked
 * before the request so a missing reason is an inline correction, not a 400.
 */
export function teamAccessChoiceError(
  choice: TeamAccessChoice,
  now: number = Date.now()
): string | null {
  if (choice.mode !== "comp") return null
  if (!choice.planId) return "Choose the plan to comp."
  if (!choice.reason.trim()) {
    return "A reason is required. It is what marks this subscription as a comp in Stripe and the DB."
  }
  if (choice.expiresAt) {
    const parsed = dateInputToLocalStart(choice.expiresAt)
    if (!parsed) return "The end date is not a valid date."
    if (parsed.getTime() <= now) return "The end date must be in the future."
  }
  return null
}

/**
 * The access part of the create-team request.
 *
 * `expiresAt` is an explicit `null` for a comp with no end date, never an
 * omitted key: the backend schema is nullable-not-optional so the caller has to
 * state which it means, and `JSON.stringify` would drop `undefined`. A date is
 * sent as local midnight of the chosen day (see `dateInputToLocalStart`).
 */
export function buildTeamAccessPayload(
  choice: TeamAccessChoice
): Pick<CreateTeamPayload, "comp" | "startTrial"> {
  switch (choice.mode) {
    case "comp":
      return {
        comp: {
          planId: choice.planId,
          reason: choice.reason.trim(),
          expiresAt: dateInputToIso(choice.expiresAt),
        },
      }
    case "trial":
      return { startTrial: true }
    case "none":
      return {}
  }
}

/** The submit button says exactly what will happen. */
export function teamAccessSubmitLabel(choice: TeamAccessChoice, planName?: string): string {
  switch (choice.mode) {
    case "comp":
      return `Create team and comp ${planName ?? choice.planId ?? "plan"}`
    case "trial":
      return "Create team and start trial"
    case "none":
      return "Create team"
  }
}

/**
 * The confirmation shown before a create that provisions something in Stripe.
 * A comp is a persistent external subscription and a trial is a Stripe
 * subscription with a 14-day clock on it, so both get the same confirm step
 * Comp Plan has on the team page (admin convention: sensitive operations
 * confirm first). "No plan yet" provisions nothing in Stripe, so it has no
 * confirm step. It is not purely local, though: every admin create cancels a
 * pending card-free trial the coach started in the app (the backend has done
 * that since before this chooser existed), which is why the card says so and
 * the create toast reports it.
 */
export function teamAccessConfirmCopy(
  choice: TeamAccessChoice,
  teamName: string,
  planName?: string
): { title: string; description: string; confirmLabel: string } | null {
  switch (choice.mode) {
    case "comp": {
      const plan = planName ?? choice.planId
      const ending = choice.expiresAt
        ? `, with access stopping at the start of ${choice.expiresAt}`
        : ", with no end date"
      return {
        title: `Create "${teamName}" and comp ${plan}?`,
        description: `Creates the team and a real Stripe subscription for ${plan} at 100% off${ending}. The team's access is derived from it. Undoing this means revoking the comp in Stripe.`,
        confirmLabel: "Create team and comp",
      }
    }
    case "trial":
      return {
        title: `Create "${teamName}" and start a trial?`,
        description:
          "Creates the team and a card-free 14-day Individual-plan trial in Stripe. After 14 days it cancels and access ends unless the coach upgrades in the app.",
        confirmLabel: "Create team and start trial",
      }
    case "none":
      return null
  }
}
